import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-black text-gray-300">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-white mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-12">Last updated: September 23, 2026</p>

        <div className="space-y-10 leading-relaxed">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
            <p>
              Romulus, operated by UnlEESHed Studios ("we," "us," or "our"), operates the
              website and platform at{' '}
              <span className="text-white">romulus.app</span> (the "Service"). This Privacy Policy
              explains how we collect, use, disclose, and protect your personal information when you
              use our Service.
            </p>
            <p className="mt-3">
              By using the Service, you agree to the collection and use of information in accordance
              with this policy. If you do not agree, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">2.1 Information You Provide</h3>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><span className="text-white">Account information:</span> Full name, email address, company name</li>
              <li><span className="text-white">Payment information:</span> Processed securely by Stripe. We do not store your credit card numbers, bank account details, or other payment credentials on our servers. Stripe's privacy policy governs their handling of your payment data.</li>
              <li><span className="text-white">Business preferences:</span> Target location, selected niches, and campaign settings</li>
              <li><span className="text-white">Content you create:</span> Website content, outreach messages, and customizations</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">2.2 Information Collected Automatically</h3>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><span className="text-white">Usage data:</span> Pages visited, features used, actions taken within the platform</li>
              <li><span className="text-white">Device information:</span> Browser type, operating system, screen resolution</li>
              <li><span className="text-white">Log data:</span> IP address, access times, referring URLs</li>
              <li><span className="text-white">Cookies and similar technologies:</span> See Section 9 below</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">2.3 Third-Party Data</h3>
            <p>
              Our Service uses the <span className="text-white">Google Places API</span> to retrieve
              publicly available business information, including business names, addresses, phone
              numbers, ratings, reviews, photos, and operating hours. This data is sourced from
              Google and is governed by{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-300 underline"
              >
                Google's Privacy Policy
              </a>
              . We do not guarantee the accuracy, completeness, or currentness of this data. See
              Section 12 for important disclaimers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. Information We Do Not Collect</h2>
            <p>We do not collect or process:</p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
              <li>Precise geolocation data</li>
              <li>Contacts or address book data</li>
              <li>Biometric data</li>
              <li>Health or medical information</li>
              <li>Social Security numbers or government IDs</li>
              <li>Sensitive personal information (racial or ethnic origin, religious beliefs, sexual orientation, etc.)</li>
              <li>Credit card numbers or bank account details (handled entirely by Stripe)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
              <li>Provide, operate, and maintain the Service</li>
              <li>Process payments and manage subscriptions via Stripe</li>
              <li>Generate AI-powered website content based on business data</li>
              <li>Facilitate lead discovery through Google Places data</li>
              <li>Enable outreach features (SMS/email templates)</li>
              <li>Process payouts through Stripe Connect</li>
              <li>Communicate with you about your account when necessary (e.g., via in-app notifications)</li>
              <li>Monitor usage and enforce subscription quotas</li>
              <li>Improve and develop the Service</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Legal Bases for Processing (GDPR)</h2>
            <p>If you are in the European Economic Area (EEA), UK, or Switzerland, we process your personal data under the following legal bases:</p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
              <li><span className="text-white">Contract performance:</span> To provide the Service you subscribed to</li>
              <li><span className="text-white">Legitimate interests:</span> To improve our Service, prevent fraud, and ensure security</li>
              <li><span className="text-white">Consent:</span> Where you have given explicit consent (e.g., analytics cookies, marketing communications)</li>
              <li><span className="text-white">Legal obligation:</span> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. Data Sharing and Disclosure</h2>
            <p>We share your information only in the following circumstances:</p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">6.1 Service Providers</h3>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><span className="text-white">Stripe:</span> Payment processing, subscription management, and Connect payouts</li>
              <li><span className="text-white">Firebase (Google Cloud):</span> Authentication, database, hosting, and cloud functions</li>
              <li><span className="text-white">Firebase Analytics (Google):</span> Anonymous usage analytics to help us understand how visitors interact with the Service (pages visited, signup funnel, feature usage). For users in the EEA, UK, and Switzerland, analytics is disabled by default and only enabled after you provide explicit consent via our cookie banner. For users outside these regions, analytics is enabled by default and can be disabled by contacting us. We use Google Consent Mode v2.</li>
              <li><span className="text-white">Advertising measurement (Google Ads, OpenAI/ChatGPT Ads, Meta):</span> When you arrive from one of our ads, we measure whether that visit led to a purchase. These providers receive the event (page viewed, checkout started, purchase and its amount), a click identifier from the ad, and — at checkout — a one-way SHA-256 hash of your email address so the purchase can be matched to the click. We never send them your email address, name, or payment details in readable form. Subject to the same consent gate as analytics: disabled by default in the EEA, UK, and Switzerland until you click "Accept".</li>
              <li><span className="text-white">Google Places API:</span> Business data retrieval for lead generation</li>
              <li><span className="text-white">Google Generative AI (Gemini) &amp; Anthropic (Claude):</span> AI-powered content generation. Only business lead data (business name, location, niche, ratings, and reviews) is sent to these services to generate website content. Your personal account information (email, payment details, account settings) is never sent to AI providers. These providers process data according to their respective privacy policies and do not use your data to train their models.</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">6.2 Other Disclosures</h3>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>When required by law, regulation, or legal process</li>
              <li>To protect the rights, safety, or property of Romulus, our users, or the public</li>
              <li>In connection with a merger, acquisition, or sale of assets (with notice to you)</li>
            </ul>

            <p className="mt-4">We do <span className="text-white font-semibold">not</span> sell your personal information to third parties. Beyond the advertising-measurement data described in 6.1, we do not share your data with advertisers, and we do not sell or disclose it for cross-context behavioral advertising. We do not use your data to train artificial intelligence models.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. International Data Transfers</h2>
            <p>
              Your data may be transferred to and processed in countries outside your country of
              residence, including the United States. These countries may have different data
              protection laws. When we transfer data internationally, we use appropriate safeguards
              such as Standard Contractual Clauses (SCCs) approved by the European Commission,
              or rely on the service provider's compliance frameworks (e.g., Google's and Stripe's
              data processing agreements).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. Data Retention</h2>
            <p>
              We retain your personal data for as long as your account is active or as needed to
              provide the Service. After account deletion, your personal data is deleted
              immediately from our active systems. Certain records may be retained if required by
              applicable law (e.g., tax or accounting obligations), but only for as long as
              legally necessary.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">9. Cookies and Tracking Technologies</h2>
            <p>We use the following cookies and similar technologies:</p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
              <li><span className="text-white">Essential cookies:</span> Required for authentication and core functionality (Firebase session tokens). These cannot be disabled.</li>
              <li><span className="text-white">Stripe cookies:</span> Set by Stripe during payment processing for security and fraud prevention.</li>
              <li><span className="text-white">Analytics cookies (Firebase Analytics / Google Analytics for Firebase):</span> Used to measure usage of the Service and improve user experience. These cookies collect aggregated, pseudonymous information such as pages visited, button clicks, signup funnel steps, and approximate device/browser information. <span className="text-white font-semibold">For users in the EEA, UK, and Switzerland, these cookies are disabled by default</span> and only set after you affirmatively click "Accept" in our cookie banner. For users outside these regions, these cookies are enabled by default in accordance with applicable law (e.g., CCPA opt-out model), and you may opt out at any time by contacting us. We implement Google Consent Mode v2.</li>
              <li><span className="text-white">Advertising measurement cookies (Google Ads, OpenAI/ChatGPT Ads, Meta):</span> Set when you arrive from one of our ads, to record whether that ad led to a purchase. The OpenAI pixel stores a click reference for up to 30 days and a browser reference for up to 365 days. The Meta pixel stores browser and click identifiers (_fbp, _fbc) for up to 90 days. <span className="text-white font-semibold">For users in the EEA, UK, and Switzerland these are disabled by default</span> and the pixel is not loaded at all until you click "Accept" in our cookie banner.</li>
            </ul>
            <p className="mt-4">
              You can control cookies through your browser settings. Disabling essential cookies may
              prevent you from using the Service. EEA/UK/Swiss visitors can withdraw analytics
              consent at any time by clearing site data in their browser, which will re-display the
              consent banner on the next visit.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">9.1 Do-Not-Track Signals</h3>
            <p>
              Some browsers include a Do-Not-Track ("DNT") feature that signals websites not to
              track your browsing activity. Because no uniform standard for interpreting DNT signals
              has been adopted, we do not currently respond to DNT browser signals. If a standard
              is established that we must follow, we will update this policy accordingly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">10. Your Rights</h2>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">10.1 Rights Under GDPR (EEA/UK/Switzerland)</h3>
            <p>You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-2">
              <li><span className="text-white">Access:</span> Request a copy of the personal data we hold about you</li>
              <li><span className="text-white">Rectification:</span> Request correction of inaccurate or incomplete data</li>
              <li><span className="text-white">Erasure:</span> Request deletion of your personal data ("right to be forgotten")</li>
              <li><span className="text-white">Restriction:</span> Request that we limit processing of your data</li>
              <li><span className="text-white">Data portability:</span> Request a machine-readable copy of your data</li>
              <li><span className="text-white">Objection:</span> Object to processing based on legitimate interests</li>
              <li><span className="text-white">Withdraw consent:</span> Where processing is based on consent, withdraw it at any time</li>
            </ul>
            <p className="mt-3">
              You also have the right to lodge a complaint with your local data protection authority.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">10.2 Rights Under CCPA/CPRA (California Residents)</h3>
            <p>California residents have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-2">
              <li><span className="text-white">Know:</span> What personal information we collect and how it is used</li>
              <li><span className="text-white">Delete:</span> Request deletion of your personal information</li>
              <li><span className="text-white">Opt-out:</span> Opt out of the sale or sharing of personal information (we do not sell your data)</li>
              <li><span className="text-white">Non-discrimination:</span> We will not discriminate against you for exercising your rights</li>
              <li><span className="text-white">Correction:</span> Request correction of inaccurate personal information</li>
              <li><span className="text-white">Limit use of sensitive data:</span> Direct us to limit the use of your sensitive personal information</li>
            </ul>
            <p className="mt-3">
              In the preceding 12 months, we have collected the categories of personal information
              described in Section 2. We do not sell personal information. To exercise your rights,
              contact us at{' '}
              <a href="mailto:unleeshedstudios@gmail.com" className="text-primary-400 hover:text-primary-300 underline">
                unleeshedstudios@gmail.com
              </a>.
              We will respond within 45 days.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">10.3 How to Exercise Your Rights</h3>
            <p>
              To exercise any of your privacy rights, email us at{' '}
              <a href="mailto:unleeshedstudios@gmail.com" className="text-primary-400 hover:text-primary-300 underline">
                unleeshedstudios@gmail.com
              </a>.
              You may also designate an authorized agent to submit a request on your behalf. An
              authorized agent must provide written, signed permission from you and we may require
              you to verify your identity directly with us.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">10.4 Request Verification</h3>
            <p>
              When you submit a privacy request, we will verify your identity before processing it.
              We may ask you to confirm your email address or provide additional information to match
              you with our records. This is to protect your data from unauthorized access.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">10.5 Appeals</h3>
            <p>
              If we decline to take action on your privacy request, you may appeal by emailing us
              at{' '}
              <a href="mailto:unleeshedstudios@gmail.com" className="text-primary-400 hover:text-primary-300 underline">
                unleeshedstudios@gmail.com
              </a>{' '}
              with the subject line "Privacy Request Appeal." We will respond in writing within 45
              days with our decision and reasoning. If your appeal is denied, you may contact your
              state attorney general or relevant data protection authority.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">10.6 Additional Rights (Other Jurisdictions)</h3>
            <p>
              If you are located in Brazil (LGPD), Canada (PIPEDA), Australia (Privacy Act 1988),
              or other jurisdictions with data protection laws, you may have similar rights to
              access, correct, and delete your personal data. Contact us to exercise these rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">11. Account Deletion</h2>
            <p>
              You can delete your account at any time from your{' '}
              <span className="text-white">Settings</span> page within the dashboard. Upon deletion:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
              <li>Your account and profile information will be permanently deleted</li>
              <li>Your leads, campaigns, generated sites, and associated data will be removed</li>
              <li>Your Stripe subscription will be canceled</li>
              <li>Data deletion is performed immediately upon account deletion</li>
              <li>Certain records may be retained only if required by applicable law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">12. Important Disclaimers</h2>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">12.1 Data Accuracy</h3>
            <p>
              Business information displayed on the Service (including names, addresses, phone
              numbers, ratings, reviews, and photos) is sourced from Google Places and other
              third-party sources.{' '}
              <span className="text-white font-semibold">
                We do not guarantee and are not responsible for the accuracy, completeness, or
                currentness of this data.
              </span>{' '}
              Users should independently verify all business information before relying on it.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">12.2 AI-Generated Content</h3>
            <p>
              Website content generated by our Service uses artificial intelligence (Google Gemini
              and Anthropic Claude).{' '}
              <span className="text-white font-semibold">
                AI-generated content may contain inaccuracies, errors, or fabricated information.
              </span>{' '}
              Users are solely responsible for reviewing, verifying, and editing all generated
              content before publishing or sharing it. Romulus is not liable for any claims,
              damages, or losses arising from the use of AI-generated content.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">12.3 Google Places Photos</h3>
            <p>
              Photos displayed in generated websites may be sourced from Google Places. These
              photos are subject to Google's terms of service and may be protected by copyright.
              Users are responsible for ensuring they have the right to use any photos included in
              their generated websites. Romulus does not claim ownership of third-party photos and
              makes no warranties regarding the right to use them.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">12.4 User Conduct and Spam</h3>
            <p>
              Romulus provides tools for outreach (SMS and email templates).{' '}
              <span className="text-white font-semibold">
                Romulus is not responsible for how users utilize these tools.
              </span>{' '}
              Users are solely responsible for complying with all applicable laws when conducting
              outreach, including but not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-2">
              <li><span className="text-white">CAN-SPAM Act (US):</span> Commercial emails must include accurate headers, a valid physical address, and a clear unsubscribe mechanism</li>
              <li><span className="text-white">TCPA (US):</span> Text messages and calls require prior express consent from recipients</li>
              <li><span className="text-white">GDPR (EU/UK):</span> Marketing communications require opt-in consent</li>
              <li><span className="text-white">CASL (Canada):</span> Commercial electronic messages require express or implied consent</li>
              <li><span className="text-white">Spam Act 2003 (Australia):</span> Commercial messages must include sender identification and an unsubscribe option</li>
            </ul>
            <p className="mt-3">
              <span className="text-white font-semibold">
                Any spam, unsolicited bulk messaging, harassment, or other misuse of the outreach
                tools is strictly prohibited and may result in immediate account termination.
              </span>{' '}
              Romulus disclaims all liability for any consequences arising from user-initiated
              outreach.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">13. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your data, including
              encryption in transit (TLS/SSL), secure authentication via Firebase, and PCI-compliant
              payment processing through Stripe. However, no method of transmission or storage is
              100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">14. Data Breach Notification</h2>
            <p>
              If a security breach occurs that compromises your personal information, we will notify
              affected users as required by applicable law. For users in the EEA/UK, we will notify
              the relevant supervisory authority within 72 hours of becoming aware of a breach, where
              required under GDPR. We will also notify affected individuals without undue delay when
              the breach is likely to result in a high risk to their rights and freedoms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">15. Children's Privacy</h2>
            <p>
              The Service is not intended for anyone under the age of 18. We do not knowingly
              collect personal information from individuals under 18. If we learn that we have
              collected data from someone under 18, we will delete it promptly. If you believe a
              minor has provided us with personal data, please contact us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">16. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material
              changes by posting the updated policy on this page and updating the "Last updated"
              date. For significant changes, we may also notify you via in-app notice. Your continued use of
              the Service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">17. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy, wish to exercise your data rights, or
              have a privacy concern, contact us at:
            </p>
            <p className="mt-3">
              <span className="text-white">Romulus</span><br />
              Operated by UnlEESHed Studios<br />
              Atlanta, GA, United States<br />
              Email:{' '}
              <a href="mailto:unleeshedstudios@gmail.com" className="text-primary-400 hover:text-primary-300 underline">
                unleeshedstudios@gmail.com
              </a>
            </p>
            <p className="mt-3 text-gray-500 text-sm">
              We aim to respond to all privacy-related inquiries within 30 days.
            </p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex items-center justify-between text-sm text-gray-500">
          <span>&copy; {new Date().getFullYear()} Romulus. All rights reserved.</span>
          <Link to="/terms" className="text-primary-400 hover:text-primary-300 transition-colors">
            Terms of Service
          </Link>
        </div>
      </div>
    </div>
  );
}
